// Sugar Alarm — cycle 475: the onboarding presentation of ProviderPicker
// (the Shield Glow "Connect your CGM" step). Reached ONLY through
// `<ProviderPicker presentation="onboarding">`; the default presentation
// (Settings CgmConnectModal, the Members-pane picker) never renders this.
//
// Three full-width rows, never two columns: Dexcom, FreeStyle Libre, a quiet
// OR divider, then the household row, then "Connect later". Each row is ONE
// native button (no nested interactive element) carrying its own accessible
// name. A tap calls the real callback at once — nothing latches a selection.
//
// The provider objects, names, model labels and glyphs come from
// `providers.tsx` unchanged (the model label is upper-cased by CSS only; the
// accessible name lists the models with commas). The one-line descriptions
// are this screen's copy, so they live here, not in the shared catalog.

import { ArrowRightIcon } from "../../components/ui/icons";
import { Logo } from "../../components/ui/Logo";
import type { Provider, ProviderId } from "./providers";
import { PROVIDERS } from "./providers";
import s from "./ShieldGlow.module.css";

const DESCRIPTION: Record<ProviderId, string> = {
  dexcom: "Connect your Dexcom CGM",
  libre: "Connect with LibreLinkUp",
};

const ROW_CLASS: Record<ProviderId, string | undefined> = {
  dexcom: undefined,
  libre: s.libre,
};

/** "G7 · G6" → "G7, G6" for the accessible name. */
function modelsForName(models: string): string {
  return models
    .split("·")
    .map((m) => m.trim())
    .filter(Boolean)
    .join(", ");
}

function cx(...names: Array<string | undefined | false>): string {
  return names.filter(Boolean).join(" ");
}

interface Props {
  onPick: (p: Provider) => void;
  onSkip?: () => void;
  /** Already resolved by ProviderPicker: `showPairDevice && onPairDevice`. */
  onPairDevice?: () => void;
}

export function OnboardingProviderChoices({ onPick, onSkip, onPairDevice }: Props) {
  return (
    <div className={s.picker} data-presentation="onboarding">
      <div className={s.list}>
        {PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className={cx(s.card, ROW_CLASS[p.id])}
            data-choice={p.id}
            aria-label={`Connect ${p.name} (${modelsForName(p.models)})`}
            onClick={() => onPick(p)}
          >
            <span className={s.icon} aria-hidden="true">
              {p.glyph(26)}
            </span>
            <span className={s.copy}>
              <span className={s.titleRow}>
                <span className={s.name}>{p.name}</span>
                <span className={s.model}>{p.models}</span>
              </span>
              <span className={s.desc}>{DESCRIPTION[p.id]}</span>
            </span>
            <span className={s.action} aria-hidden="true">
              <span className={s.actionLabel}>Connect</span>
              <span className={s.arrow}>
                <ArrowRightIcon size={14} />
              </span>
            </span>
          </button>
        ))}

        {onPairDevice ? (
          <>
            <div className={s.divider} data-choice-divider="" aria-hidden="true">
              OR
            </div>
            <button
              type="button"
              className={cx(s.card, s.household)}
              data-choice="household"
              aria-label="Pair with your Sugar Alarm household"
              onClick={onPairDevice}
            >
              <span className={s.icon} aria-hidden="true">
                <Logo size={30} alt="" />
              </span>
              <span className={s.copy}>
                <span className={s.titleRow}>
                  <span className={s.name}>Already using Sugar Alarm?</span>
                </span>
                <span className={s.desc}>
                  Pair with your household. No CGM setup needed here.
                </span>
              </span>
              <span className={s.action} aria-hidden="true">
                <span className={s.actionLabel}>Pair</span>
                <span className={s.arrow}>
                  <ArrowRightIcon size={14} />
                </span>
              </span>
            </button>
          </>
        ) : null}
      </div>

      {onSkip ? (
        <button type="button" className={s.later} onClick={onSkip}>
          Connect later
        </button>
      ) : null}
    </div>
  );
}
